import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageIcon,
  PageDescription,
  ContentGrid,
  Card,
  CardTitle,
  CardContent,
  ActionBar,
  Button,
  SearchInput,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  StatusBadge,
  TabContainer,
  TabList,
  Tab,
  TabContent,
  fadeIn
} from '../styles/PageStyles';
import { faWalkieTalkie } from '@fortawesome/free-solid-svg-icons';
import PageHeaderWithInfo from '../components/common/PageHeaderWithInfo';

const Observability = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('system');

  // Mock data for system health
  const systemMetrics = [
    {
      id: 'SYS-001',
      name: 'API Gateway',
      status: 'healthy',
      cpu: '32%',
      memory: '45%',
      latency: '45ms',
      throughput: '1250 req/min',
      errorRate: '0.02%',
      uptime: '99.99%'
    },
    {
      id: 'SYS-002',
      name: 'Agent Service',
      status: 'healthy',
      cpu: '48%',
      memory: '60%',
      latency: '120ms',
      throughput: '850 req/min',
      errorRate: '0.05%',
      uptime: '99.98%'
    },
    {
      id: 'SYS-003',
      name: 'Knowledge Base',
      status: 'healthy',
      cpu: '25%',
      memory: '38%',
      latency: '65ms',
      throughput: '950 req/min',
      errorRate: '0.01%',
      uptime: '99.99%'
    },
    {
      id: 'SYS-004',
      name: 'Document Processor',
      status: 'warning',
      cpu: '78%',
      memory: '82%',
      latency: '210ms',
      throughput: '650 req/min',
      errorRate: '0.8%',
      uptime: '99.95%'
    },
    {
      id: 'SYS-005',
      name: 'Model Service',
      status: 'healthy',
      cpu: '55%',
      memory: '65%',
      latency: '180ms',
      throughput: '720 req/min',
      errorRate: '0.1%',
      uptime: '99.97%'
    },
  ];

  // Mock data for active agents
  const activeAgents = [
    {
      id: 'AGT-001',
      name: 'Claims Processor',
      status: 'active',
      type: 'Processing',
      tasks: '125',
      avgResponseTime: '1.8s',
      successRate: '98.5%',
      lastActive: '2 min ago',
      model: 'GPT-5'
    },
    {
      id: 'AGT-002',
      name: 'Underwriting Assistant',
      status: 'active',
      type: 'Analysis',
      tasks: '78',
      avgResponseTime: '2.5s',
      successRate: '97.2%',
      lastActive: '5 min ago',
      model: 'GPT-5'
    },
    {
      id: 'AGT-003',
      name: 'Customer Service Bot',
      status: 'active',
      type: 'Interaction',
      tasks: '215',
      avgResponseTime: '1.2s',
      successRate: '99.1%',
      lastActive: '1 min ago',
      model: 'Claude-3'
    },
    {
      id: 'AGT-004',
      name: 'Fraud Detector',
      status: 'active',
      type: 'Analysis',
      tasks: '45',
      avgResponseTime: '3.5s',
      successRate: '96.8%',
      lastActive: '8 min ago',
      model: 'GPT-5'
    },
    {
      id: 'AGT-005',
      name: 'Document Analyzer',
      status: 'warning',
      type: 'Processing',
      tasks: '95',
      avgResponseTime: '4.2s',
      successRate: '94.5%',
      lastActive: '3 min ago',
      model: 'Claude-3'
    },
  ];

  // Mock data for alerts
  const alerts = [
    {
      id: 'ALT-001',
      timestamp: '2025-03-17 15:45:22',
      service: 'Document Processor',
      severity: 'warning',
      message: 'High CPU utilization (78%)',
      status: 'active'
    },
    {
      id: 'ALT-002',
      timestamp: '2025-03-17 15:30:18',
      service: 'Document Analyzer Agent',
      severity: 'warning',
      message: 'Response time degradation detected',
      status: 'active'
    },
    {
      id: 'ALT-003',
      timestamp: '2025-03-17 14:15:05',
      service: 'API Gateway',
      severity: 'info',
      message: 'Throughput spike detected (2000 req/min)',
      status: 'resolved'
    },
    {
      id: 'ALT-004',
      timestamp: '2025-03-17 12:55:30',
      service: 'Knowledge Base',
      severity: 'critical',
      message: 'Connection timeout to document store',
      status: 'resolved'
    },
    {
      id: 'ALT-005',
      timestamp: '2025-03-17 10:40:12',
      service: 'Model Service',
      severity: 'warning',
      message: 'Increased error rate (0.5%)',
      status: 'resolved'
    },
  ];

  // Filter data based on search term
  const filteredSystemMetrics = systemMetrics.filter(metric =>
    metric.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredActiveAgents = activeAgents.filter(agent =>
    agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.model.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAlerts = alerts.filter(alert =>
    alert.service.toLowerCase().includes(searchTerm.toLowerCase()) ||
    alert.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
    alert.severity.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'active';
      case 'warning': return 'warning';
      case 'critical': return 'error';
      case 'active': return 'active';
      case 'resolved': return 'default';
      case 'info': return 'default';
      default: return 'default';
    }
  };

  // Get severity badge color
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'warning': return 'warning';
      case 'info': return 'default';
      default: return 'default';
    }
  };

  return (
    <PageContainer>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >

        <PageHeaderWithInfo
          icon={faWalkieTalkie}
          title="Observability"
          description="Monitor system health, active agents, and performance metrics.
          Get real-time insights into the operation of your AI ecosystem."
        >
          <div style={{ display: 'flex', gap: '1rem' }}>
            {activeTab === 'applications' && (
              <SearchInput
                type="text"
                placeholder="Search claims..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            )}

            <div style={{ display: 'flex', gap: '1rem' }}>
            <Button>Export Metrics</Button>
            <Button primary>Configure Alerts</Button>
            </div>
          </div>
        </PageHeaderWithInfo>


        <ContentGrid>
          <Card>
            <CardTitle>System Overview</CardTitle>
            <CardContent>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                <div style={{
                  flex: '1 1 20%',
                  minWidth: '150px',
                  background: 'rgba(52, 199, 89, 0.1)',
                  borderRadius: '8px',
                  padding: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <div style={{ fontSize: '2rem', color: '#34C759' }}>5</div>
                  <div style={{ textAlign: 'center', fontSize: '0.9rem' }}>Services</div>
                </div>

                <div style={{
                  flex: '1 1 20%',
                  minWidth: '150px',
                  background: 'rgba(0, 102, 255, 0.1)',
                  borderRadius: '8px',
                  padding: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <div style={{ fontSize: '2rem', color: 'var(--primary)' }}>5</div>
                  <div style={{ textAlign: 'center', fontSize: '0.9rem' }}>Active Agents</div>
                </div>

                <div style={{
                  flex: '1 1 20%',
                  minWidth: '150px',
                  background: 'rgba(255, 204, 0, 0.1)',
                  borderRadius: '8px',
                  padding: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <div style={{ fontSize: '2rem', color: '#FFCC00' }}>2</div>
                  <div style={{ textAlign: 'center', fontSize: '0.9rem' }}>Active Alerts</div>
                </div>

                <div style={{
                  flex: '1 1 20%',
                  minWidth: '150px',
                  background: 'rgba(52, 199, 89, 0.1)',
                  borderRadius: '8px',
                  padding: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <div style={{ fontSize: '2rem', color: '#34C759' }}>99.97%</div>
                  <div style={{ textAlign: 'center', fontSize: '0.9rem' }}>System Uptime</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardTitle>System Load</CardTitle>
            <CardContent>
              <div style={{ height: '150px', display: 'flex', alignItems: 'flex-end', gap: '8px', marginTop: '20px' }}>
                {[65, 72, 58, 80, 75, 85, 78, 90, 82, 75, 68, 72].map((value, index) => (
                  <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{
                      height: `${value * 1.2}px`,
                      width: '100%',
                      background: value > 80 ? '#FFCC00' : 'var(--primary)',
                      borderRadius: '4px 4px 0 0'
                    }}></div>
                    <div style={{ fontSize: '0.7rem', marginTop: '5px' }}>{index + 1}h</div>
                  </div>
                ))}
              </div>
              <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                System Load - Last 12 Hours
              </div>
            </CardContent>
          </Card>
        </ContentGrid>

        <TabContainer style={{ marginTop: '1.5rem' }}>
          <TabList>
            <Tab active={activeTab === 'system'} onClick={() => setActiveTab('system')}>System Health</Tab>
            <Tab active={activeTab === 'agents'} onClick={() => setActiveTab('agents')}>Active Agents</Tab>
            <Tab active={activeTab === 'alerts'} onClick={() => setActiveTab('alerts')}>Alerts</Tab>
            <Tab active={activeTab === 'logs'} onClick={() => setActiveTab('logs')}>Logs</Tab>
          </TabList>

          <ActionBar>
            <SearchInput
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button>Filter</Button>
            <Button>Sort</Button>
          </ActionBar>

          <TabContent active={activeTab === 'system'}>
            <Card>
              <CardTitle>System Health Metrics</CardTitle>
              <Table>
                <thead>
                  <tr>
                    <TableHeader>ID</TableHeader>
                    <TableHeader>Service</TableHeader>
                    <TableHeader>CPU</TableHeader>
                    <TableHeader>Memory</TableHeader>
                    <TableHeader>Latency</TableHeader>
                    <TableHeader>Throughput</TableHeader>
                    <TableHeader>Error Rate</TableHeader>
                    <TableHeader>Uptime</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {filteredSystemMetrics.map(metric => (
                    <TableRow key={metric.id}>
                      <TableCell>{metric.id}</TableCell>
                      <TableCell>{metric.name}</TableCell>
                      <TableCell>{metric.cpu}</TableCell>
                      <TableCell>{metric.memory}</TableCell>
                      <TableCell>{metric.latency}</TableCell>
                      <TableCell>{metric.throughput}</TableCell>
                      <TableCell>{metric.errorRate}</TableCell>
                      <TableCell>{metric.uptime}</TableCell>
                      <TableCell>
                        <StatusBadge status={getStatusColor(metric.status)}>
                          {metric.status.charAt(0).toUpperCase() + metric.status.slice(1)}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Button style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Details</Button>
                          <Button primary style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Restart</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </Card>
          </TabContent>

          <TabContent active={activeTab === 'agents'}>
            <Card>
              <CardTitle>Active Agents</CardTitle>
              <Table>
                <thead>
                  <tr>
                    <TableHeader>ID</TableHeader>
                    <TableHeader>Agent</TableHeader>
                    <TableHeader>Type</TableHeader>
                    <TableHeader>Model</TableHeader>
                    <TableHeader>Tasks</TableHeader>
                    <TableHeader>Avg Response</TableHeader>
                    <TableHeader>Success Rate</TableHeader>
                    <TableHeader>Last Active</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {filteredActiveAgents.map(agent => (
                    <TableRow key={agent.id}>
                      <TableCell>{agent.id}</TableCell>
                      <TableCell>{agent.name}</TableCell>
                      <TableCell>{agent.type}</TableCell>
                      <TableCell>{agent.model}</TableCell>
                      <TableCell>{agent.tasks}</TableCell>
                      <TableCell>{agent.avgResponseTime}</TableCell>
                      <TableCell>{agent.successRate}</TableCell>
                      <TableCell>{agent.lastActive}</TableCell>
                      <TableCell>
                        <StatusBadge status={getStatusColor(agent.status)}>
                          {agent.status.charAt(0).toUpperCase() + agent.status.slice(1)}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Button style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Details</Button>
                          <Button primary style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Pause</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </Card>
          </TabContent>

          <TabContent active={activeTab === 'alerts'}>
            <Card>
              <CardTitle>System Alerts</CardTitle>
              <Table>
                <thead>
                  <tr>
                    <TableHeader>ID</TableHeader>
                    <TableHeader>Timestamp</TableHeader>
                    <TableHeader>Service</TableHeader>
                    <TableHeader>Message</TableHeader>
                    <TableHeader>Severity</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {filteredAlerts.map(alert => (
                    <TableRow key={alert.id}>
                      <TableCell>{alert.id}</TableCell>
                      <TableCell>{alert.timestamp}</TableCell>
                      <TableCell>{alert.service}</TableCell>
                      <TableCell>{alert.message}</TableCell>
                      <TableCell>
                        <StatusBadge status={getSeverityColor(alert.severity)}>
                          {alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={getStatusColor(alert.status)}>
                          {alert.status.charAt(0).toUpperCase() + alert.status.slice(1)}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Button style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Details</Button>
                          {alert.status === 'active' && (
                            <Button primary style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Resolve</Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </Card>
          </TabContent>

          <TabContent active={activeTab === 'logs'}>
            <Card>
              <CardTitle>System Logs</CardTitle>
              <CardContent>
                <div style={{
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  background: 'rgba(0, 0, 0, 0.05)',
                  padding: '1rem',
                  borderRadius: '8px',
                  height: '400px',
                  overflowY: 'auto'
                }}>
                  <div style={{ color: 'var(--text-secondary)' }}>[2025-03-17 15:45:22] [INFO] API Gateway: Processed 1250 requests in the last minute</div>
                  <div style={{ color: '#FFCC00' }}>[2025-03-17 15:45:22] [WARN] Document Processor: CPU utilization at 78%</div>
                  <div style={{ color: 'var(--text-secondary)' }}>[2025-03-17 15:44:15] [INFO] Agent Service: Agent 'Claims Processor' processed task #12345</div>
                  <div style={{ color: 'var(--text-secondary)' }}>[2025-03-17 15:43:30] [INFO] Knowledge Base: Successfully retrieved document #DOC-789</div>
                  <div style={{ color: '#FFCC00' }}>[2025-03-17 15:42:18] [WARN] Document Analyzer Agent: Response time increased to 4.2s</div>
                  <div style={{ color: 'var(--text-secondary)' }}>[2025-03-17 15:41:05] [INFO] Model Service: Model 'GPT-5' processed 720 requests in the last minute</div>
                  <div style={{ color: 'var(--text-secondary)' }}>[2025-03-17 15:40:22] [INFO] API Gateway: Authentication successful for user 'jsmith'</div>
                  <div style={{ color: 'var(--text-secondary)' }}>[2025-03-17 15:39:45] [INFO] Agent Service: Agent 'Underwriting Assistant' processed task #12344</div>
                  <div style={{ color: 'var(--text-secondary)' }}>[2025-03-17 15:38:30] [INFO] Knowledge Base: Successfully retrieved document #DOC-788</div>
                  <div style={{ color: 'var(--text-secondary)' }}>[2025-03-17 15:37:15] [INFO] Model Service: Model 'Claude-3' processed 650 requests in the last minute</div>
                  <div style={{ color: 'var(--text-secondary)' }}>[2025-03-17 15:36:22] [INFO] API Gateway: Authentication successful for user 'admin'</div>
                  <div style={{ color: 'var(--text-secondary)' }}>[2025-03-17 15:35:45] [INFO] Agent Service: Agent 'Customer Service Bot' processed task #12343</div>
                  <div style={{ color: 'var(--text-secondary)' }}>[2025-03-17 15:34:30] [INFO] Knowledge Base: Successfully retrieved document #DOC-787</div>
                  <div style={{ color: 'var(--text-secondary)' }}>[2025-03-17 15:33:15] [INFO] Model Service: Model 'GPT-5' processed 715 requests in the last minute</div>
                  <div style={{ color: 'var(--text-secondary)' }}>[2025-03-17 15:32:22] [INFO] API Gateway: Authentication successful for user 'jdoe'</div>
                  <div style={{ color: 'var(--text-secondary)' }}>[2025-03-17 15:31:45] [INFO] Agent Service: Agent 'Fraud Detector' processed task #12342</div>
                  <div style={{ color: 'var(--text-secondary)' }}>[2025-03-17 15:30:30] [INFO] Knowledge Base: Successfully retrieved document #DOC-786</div>
                  <div style={{ color: '#FF3B30' }}>[2025-03-17 15:30:18] [ERROR] Document Analyzer Agent: Failed to process document #DOC-785</div>
                  <div style={{ color: 'var(--text-secondary)' }}>[2025-03-17 15:29:15] [INFO] Model Service: Model 'Claude-3' processed 645 requests in the last minute</div>
                  <div style={{ color: 'var(--text-secondary)' }}>[2025-03-17 15:28:22] [INFO] API Gateway: Authentication successful for user 'msmith'</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
                  <div>
                    <Button>Download Logs</Button>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button>Previous</Button>
                    <Button>Next</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabContent>
        </TabContainer>

        <Card style={{ marginTop: '2rem' }}>
          <CardTitle>Monitoring Dashboard</CardTitle>
          <CardContent>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '1.5rem',
              flexWrap: 'wrap',
              gap: '2rem'
            }}>
              <div style={{
                flex: '1 1 30%',
                minWidth: '250px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <div style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '10px',
                  background: 'rgba(0, 102, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  <div style={{ fontSize: '2rem' }}>⌬</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Real-time</div>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textAlign: 'center', maxWidth: '250px' }}>
                  Live monitoring of system health, agent activity, and performance metrics
                </div>
              </div>

              <div style={{
                flex: '1 1 30%',
                minWidth: '250px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <div style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '10px',
                  background: 'rgba(0, 102, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  <div style={{ fontSize: '2rem' }}>⌬</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Alerting</div>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textAlign: 'center', maxWidth: '250px' }}>
                  Proactive notification system for issues requiring attention
                </div>
              </div>

              <div style={{
                flex: '1 1 30%',
                minWidth: '250px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <div style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '10px',
                  background: 'rgba(0, 102, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  <div style={{ fontSize: '2rem' }}>⌬</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Analytics</div>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textAlign: 'center', maxWidth: '250px' }}>
                  Historical data analysis and trend identification for system optimization
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </PageContainer>
  );
};

export default Observability;
